import React, { useState } from 'react';
import { styled } from 'linaria/react';
import { Link } from 'react-router-dom';
import { DynamicCategoryRenderer } from '../StartPage/ContentRendererComponents/DynamicCategoryRenderer/DynamicCategoryRenderer';
import { allComponents } from '../StartPage/ContentRendererComponents/allcomponents';
import { theme } from '../Theme';

const BrandsPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 4rem;
`;

const BrandsListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 1rem;
  max-width: 92rem;

  h2 {
    margin-bottom: 1.5rem;
  }
`;

const AlphabetNav = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 4rem;
  width: 100%;
  gap: 0.5rem;
`;

const AlphabetButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  text-decoration: none;
  cursor: pointer;
  background-color: ${props =>
    props.active ? theme.colors.clubhooksgreen : theme.colors.white};
  color: ${props => (props.active ? theme.colors.white : theme.colors.black)};
  border: 1px solid ${theme.colors.lightgrey};

  &:hover {
    color: ${theme.colors.white};
    background-color: ${props =>
      props.active ? theme.colors.clubhooksgreen : theme.colors.clubhooksgreen};
  }
`;

const BrandSection = styled.div`
  width: 100%;
  margin-bottom: 2rem;

  h3 {
    padding-bottom: 0.5rem;
    border-bottom: 1px solid ${theme.colors.lightgrey};
    margin-bottom: 1rem;
  }

  .brands-list {
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
    color: ${theme.colors.grey};
  }
`;

const BrandLink = styled(Link)`
  text-decoration: none;
  color: inherit;

  &:hover {
    color: ${theme.colors.black};
    text-decoration: underline;
    cursor: pointer;
  }
`;

export const BrandsPage = ({ category }) => {
  const [activeFilter, setActiveFilter] = useState('all');

  // Group brands by first character
  const groupBrandsByFirstChar = brands => {
    if (!brands) return {};

    const grouped = {};

    brands.forEach(brand => {
      if (!brand?.name) return;

      const firstChar = brand.name.charAt(0).toUpperCase();
      let key = firstChar;

      // Check if the first character is a number
      if (/^\d$/.test(firstChar)) {
        key = '0-9';
      }

      if (!grouped[key]) {
        grouped[key] = [];
      }

      grouped[key].push(brand);
    });

    // Sort each group alphabetically
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    });

    return grouped;
  };

  const groupedBrands = groupBrandsByFirstChar(category?.subcategories);

  // Create an array of available letters for the navigation
  const availableLetters = Object.keys(groupedBrands).sort((a, b) => {
    // Special sort for Swedish alphabet and numbers
    if (a === '0-9') return -1;
    if (b === '0-9') return 1;

    // Handle Swedish special characters
    return a.localeCompare(b, 'sv');
  });

  // Filter brands based on active filter
  const filteredBrands =
    activeFilter === 'all'
      ? groupedBrands
      : { [activeFilter]: groupedBrands[activeFilter] };

  return (
    <BrandsPageWrapper>
      <DynamicCategoryRenderer
        categoryId={category?.id}
        rendererComponents={allComponents}
      />

      <BrandsListWrapper>
        <h2>Alla varumärken</h2>

        <AlphabetNav>
          <AlphabetButton
            onClick={() => setActiveFilter('all')}
            active={activeFilter === 'all'}
          >
            Visa alla
          </AlphabetButton>

          {groupedBrands['0-9'] && (
            <AlphabetButton
              onClick={() => setActiveFilter('0-9')}
              active={activeFilter === '0-9'}
            >
              0-9
            </AlphabetButton>
          )}

          {availableLetters
            .filter(letter => letter !== '0-9')
            .map(letter => (
              <AlphabetButton
                key={letter}
                onClick={() => setActiveFilter(letter)}
                active={activeFilter === letter}
              >
                {letter}
              </AlphabetButton>
            ))}
        </AlphabetNav>

        {activeFilter === 'all'
          ? Object.keys(filteredBrands)
              .sort((a, b) => {
                if (a === '0-9') return -1;
                if (b === '0-9') return 1;
                return a.localeCompare(b, 'sv');
              })
              .map(letter => (
                <BrandSection key={letter}>
                  <h3>{letter}</h3>
                  <div className="brands-list">
                    {filteredBrands[letter].map(brand => (
                      <div key={brand?.id} className="brand-item">
                        <BrandLink
                          to={brand?.primaryRoute?.canonicalPath || '#'}
                        >
                          {brand?.name}
                        </BrandLink>
                      </div>
                    ))}
                  </div>
                </BrandSection>
              ))
          : Object.keys(filteredBrands).map(letter => (
              <BrandSection key={letter}>
                <h3>{letter}</h3>
                <div className="brands-list">
                  {filteredBrands[letter].map(brand => (
                    <div key={brand?.id} className="brand-item">
                      <BrandLink to={brand?.primaryRoute?.canonicalPath || '#'}>
                        {brand?.name}
                      </BrandLink>
                    </div>
                  ))}
                </div>
              </BrandSection>
            ))}
      </BrandsListWrapper>
    </BrandsPageWrapper>
  );
};
